import { useEffect, useRef, useCallback, useState } from 'react';
import type { AdvanceResult } from '../types/simulation';
import { useRequestHalt } from '../utils/requestHalt';

interface WsMessage {
  type: string;
  sessionId?: string;
  fromDate?: string;
  toDate?: string;
  daysAdvanced?: number;
  simDate?: string;
  targetDate?: string;
  deltas?: AdvanceResult['deltas'];
  alertsDetails?: AdvanceResult['alertsDetails'];
  summary?: {
    totalPlantings: number;
    processed: number;
    deltasCount: number;
    alerts: number;
  };
  status?: string;
}

interface UseSimulationWebSocketOptions {
  sessionId: string | null;
  onAdvanced?: (advance: AdvanceResult) => void;
  onReset?: (payload: { simDate: string; targetDate: string }) => void;
  onStatusChange?: (status: string) => void;
}

export function useSimulationWebSocket({
  sessionId,
  onAdvanced,
  onReset,
  onStatusChange,
}: UseSimulationWebSocketOptions) {
  const { halted } = useRequestHalt();
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastAdvance, setLastAdvance] = useState<AdvanceResult | null>(null);
  const onAdvancedRef = useRef(onAdvanced);
  const onResetRef = useRef(onReset);
  const onStatusRef = useRef(onStatusChange);
  onAdvancedRef.current = onAdvanced;
  onResetRef.current = onReset;
  onStatusRef.current = onStatusChange;

  const connect = useCallback(() => {
    if (!sessionId || halted) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const ws = new WebSocket(`${protocol}//${host}/ws/simulation?sessionId=${sessionId}`);

    ws.onopen = () => setConnected(true);

    ws.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);

        if (msg.type === 'sim:advanced' && msg.summary) {
          const advance: AdvanceResult = {
            fromDate: msg.fromDate!,
            toDate: msg.toDate!,
            daysAdvanced: msg.daysAdvanced!,
            simDate: msg.simDate!,
            totalPlantings: msg.summary.totalPlantings,
            processed: msg.summary.processed,
            deltasCount: msg.summary.deltasCount,
            alerts: msg.summary.alerts,
            alertsDetails: msg.alertsDetails || [],
            deltas: msg.deltas || [],
          };
          setLastAdvance(advance);
          onAdvancedRef.current?.(advance);
        }

        if (msg.type === 'sim:reset' && msg.simDate) {
          setLastAdvance(null);
          onResetRef.current?.({
            simDate: msg.simDate,
            targetDate: msg.targetDate || msg.simDate,
          });
        }

        if (msg.type === 'sim:paused' || msg.type === 'sim:resumed' || msg.type === 'sim:cancelled') {
          onStatusRef.current?.(msg.status || msg.type.replace('sim:', ''));
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    wsRef.current = ws;
  }, [sessionId, halted]);

  useEffect(() => {
    setLastAdvance(null);
  }, [sessionId]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  useEffect(() => {
    if (!halted || !wsRef.current) {
      return;
    }

    wsRef.current.close();
    wsRef.current = null;
    setConnected(false);
  }, [halted]);

  return { connected, lastAdvance };
}
